import React, { useState } from "react";
import * as adminService from "@/shared/api/services/adminService";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { useLanguage } from "@/shared/components/shared/LanguageContext";
import { auth } from "@/shared/api/graphqlClient";
import { useToast } from "@/shared/components/ui/use-toast";
import { Toaster } from "@/shared/components/ui/toaster";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/shared/components/ui/dropdown-menu";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/shared/components/ui/alert-dialog";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription
} from "@/shared/components/ui/dialog";
import {
  Plus, Search, MoreHorizontal, Pencil, Trash2, Key, Power, PowerOff,
  Building2, CheckCircle2, XCircle, Clock, AlertTriangle, Eye, Loader2, Copy
} from "lucide-react";
import { Link } from "react-router-dom";
import { createPageUrl } from "@/shared/utils";
import MerchantForm from "@/shared/components/merchants/MerchantForm";

/* ── Scoped CSS ─────────────────────────────────────────────────── */
const MC_CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Outfit:wght@400;500;600;700;800&display=swap');

  .mc-root {
    --bg:        #060C18;
    --card:      #0E1C35;
    --card-hi:   #122040;
    --border:    rgba(255,255,255,0.055);
    --border-md: rgba(255,255,255,0.09);
    --cyan:      #22D3EE;
    --cyan-soft: rgba(34,211,238,0.08);
    --cyan-glow: rgba(34,211,238,0.18);
    --cyan-ring: rgba(34,211,238,0.22);
    --blue:      #3B82F6;
    --blue-soft: rgba(59,130,246,0.10);
    --emerald:   #10B981;
    --amber:     #FBBF24;
    --rose:      #FB7185;
    --purple:    #A78BFA;
    --sky:       #38BDF8;
    --t1: #EFF6FF;
    --t2: #93B4D4;
    --t3: #4D6E90;
    --t4: #2A4060;
    --tr: 200ms cubic-bezier(0.4,0,0.2,1);
    font-family: 'Outfit', system-ui, sans-serif;
  }

  /* ── Page wrapper ── */
  .mc-root {
    display: flex; flex-direction: column; gap: 1.5rem;
    background: #060C18;
    min-height: 100%; padding: 1.5rem;
  }

  /* ── Header ── */
  .mc-header {
    display: flex; flex-direction: column; gap: 1rem;
    animation: mc-up 0.4s ease both;
  }
  @media (min-width: 640px) {
    .mc-header { flex-direction: row; align-items: center; justify-content: space-between; }
  }
  @keyframes mc-up {
    from { opacity: 0; transform: translateY(12px); }
    to   { opacity: 1; transform: translateY(0); }
  }

  .mc-title-row { display: flex; align-items: center; gap: 0.875rem; }

  .mc-icon-badge {
    width: 2.75rem; height: 2.75rem; border-radius: 0.875rem;
    background: linear-gradient(135deg, rgba(59,130,246,0.18) 0%, rgba(34,211,238,0.10) 100%);
    border: 1px solid rgba(59,130,246,0.28);
    display: flex; align-items: center; justify-content: center; flex-shrink: 0;
    box-shadow: 0 0 0 4px rgba(59,130,246,0.06);
    transition: box-shadow var(--tr), border-color var(--tr), transform var(--tr);
  }
  .mc-icon-badge:hover {
    box-shadow: 0 0 0 6px rgba(59,130,246,0.10), 0 0 20px rgba(59,130,246,0.15);
    border-color: rgba(59,130,246,0.50); transform: scale(1.05);
  }
  .mc-icon-badge svg { color: var(--blue); width: 1.2rem; height: 1.2rem; }

  .mc-title {
    font-size: 1.5rem; font-weight: 800; color: var(--t1);
    letter-spacing: -0.025em; line-height: 1.2;
  }
  .mc-subtitle { font-size: 0.8rem; color: var(--t3); margin-top: 0.175rem; }

  /* Add merchant button */
  .mc-add-btn {
    display: inline-flex; align-items: center; gap: 0.5rem;
    padding: 0.55rem 1.125rem; border-radius: 0.75rem;
    border: 1px solid rgba(34,211,238,0.30);
    background: rgba(34,211,238,0.08);
    color: var(--cyan); font-size: 0.875rem; font-weight: 700;
    font-family: 'Outfit', system-ui, sans-serif;
    cursor: pointer; white-space: nowrap;
    transition: background var(--tr), border-color var(--tr), box-shadow var(--tr), transform var(--tr);
  }
  .mc-add-btn svg { width: 0.9rem; height: 0.9rem; }
  .mc-add-btn:hover {
    background: rgba(34,211,238,0.15);
    border-color: rgba(34,211,238,0.55);
    box-shadow: 0 0 20px rgba(34,211,238,0.20);
    transform: translateY(-1px);
  }
  .mc-add-btn:active { transform: translateY(0); }

  /* ── Divider ── */
  .mc-divider {
    height: 1px;
    background: linear-gradient(90deg, transparent 0%, rgba(34,211,238,0.28) 40%, rgba(59,130,246,0.20) 70%, transparent 100%);
    border: none; margin: 0;
    animation: mc-up 0.5s 0.05s ease both;
  }

  /* ── Error banner ── */
  .mc-error {
    background: rgba(251,113,133,0.08); border: 1px solid rgba(251,113,133,0.22);
    border-radius: 0.875rem; padding: 0.875rem 1rem;
    color: #FCA5A5;
  }
  .mc-error strong { color: var(--rose); font-weight: 700; display: block; margin-bottom: 0.25rem; font-size: 0.875rem; }
  .mc-error p { font-size: 0.8rem; margin: 0; }

  /* ── Search ── */
  .mc-search-wrap {
    position: relative; max-width: 28rem;
    animation: mc-up 0.4s 0.1s ease both;
  }
  .mc-search-icon {
    position: absolute; left: 0.75rem; top: 50%; transform: translateY(-50%);
    width: 0.875rem; height: 0.875rem; color: var(--t3); pointer-events: none;
    transition: color var(--tr);
  }
  .mc-search-wrap:focus-within .mc-search-icon { color: var(--cyan); }
  .mc-search-input {
    width: 100%; height: 2.375rem;
    padding-left: 2.25rem !important; padding-right: 0.75rem !important;
    background: var(--card) !important; border: 1px solid var(--border-md) !important;
    border-radius: 0.75rem !important; color: var(--t1) !important;
    font-size: 0.8375rem !important; font-family: 'Outfit', system-ui, sans-serif !important;
    outline: none !important;
    transition: border-color var(--tr), box-shadow var(--tr), background var(--tr) !important;
  }
  .mc-search-input::placeholder { color: var(--t3) !important; }
  .mc-search-input:hover {
    border-color: var(--cyan-ring) !important;
    background: var(--card-hi) !important;
  }
  .mc-search-input:focus {
    border-color: var(--cyan) !important;
    box-shadow: 0 0 0 3px var(--cyan-glow) !important;
    background: var(--card-hi) !important;
  }

  /* ── Table card ── */
  .mc-table-card {
    border-radius: 1rem;
    border: 1px solid var(--border);
    background: linear-gradient(155deg, #122040, #0E1C35);
    overflow: hidden;
    transition: border-color var(--tr), box-shadow var(--tr);
    animation: mc-up 0.4s 0.15s ease both;
  }
  .mc-table-card:hover {
    border-color: rgba(34,211,238,0.16);
    box-shadow: 0 4px 32px rgba(0,0,0,0.3);
  }

  /* ── Table ── */
  .mc-table {
    width: 100%; border-collapse: collapse;
    min-width: 800px;
    font-family: 'Outfit', system-ui, sans-serif;
  }

  .mc-thead-row {
    background: rgba(255,255,255,0.025);
    border-bottom: 1px solid rgba(255,255,255,0.06);
  }
  .mc-th {
    padding: 11px 16px; font-size: 10px; font-weight: 800;
    color: #3E5E7E; text-transform: uppercase; letter-spacing: 0.14em;
    white-space: nowrap; text-align: left;
  }

  .mc-tbody-row {
    border-bottom: 1px solid rgba(255,255,255,0.04);
    transition: background 0.16s; cursor: pointer;
  }
  .mc-tbody-row:last-child { border-bottom: none; }
  .mc-tbody-row:hover { background: rgba(34,211,238,0.045); }
  .mc-tbody-row:hover .mc-first-td { border-left: 3px solid var(--cyan); }

  .mc-td {
    padding: 13px 16px; font-size: 13px; font-weight: 500;
    vertical-align: middle; color: var(--t2); transition: color 0.16s;
  }
  .mc-tbody-row:hover .mc-td { color: var(--t1); }

  /* Merchant name cell */
  .mc-name-cell { display: flex; align-items: center; gap: 0.75rem; }
  .mc-merchant-icon {
    width: 2.25rem; height: 2.25rem; border-radius: 0.625rem;
    background: rgba(59,130,246,0.10); border: 1px solid rgba(59,130,246,0.20);
    display: flex; align-items: center; justify-content: center; flex-shrink: 0;
    transition: border-color 0.2s, box-shadow 0.2s;
  }
  .mc-merchant-icon svg { color: var(--blue); width: 1rem; height: 1rem; }
  .mc-tbody-row:hover .mc-merchant-icon {
    border-color: rgba(59,130,246,0.45);
    box-shadow: 0 0 10px rgba(59,130,246,0.15);
  }
  .mc-merchant-name { font-weight: 700; font-size: 0.875rem; color: var(--t1); }
  .mc-merchant-email { font-size: 0.75rem; color: var(--t3); margin-top: 0.1rem; }

  /* Code badge */
  .mc-code-badge {
    display: inline-block; padding: 0.2rem 0.5rem; border-radius: 0.375rem;
    background: rgba(255,255,255,0.04); border: 1px solid var(--border-md);
    font-family: ui-monospace, monospace; font-size: 0.72rem; color: var(--cyan);
    letter-spacing: 0.04em;
    transition: border-color 0.16s, background 0.16s;
  }
  .mc-tbody-row:hover .mc-code-badge { border-color: var(--cyan-ring); background: var(--cyan-soft); }

  /* Status badges */
  .mc-status {
    display: inline-flex; align-items: center; gap: 0.3rem;
    padding: 0.2rem 0.55rem; border-radius: 9999px;
    font-size: 0.68rem; font-weight: 700; border: 1px solid transparent;
  }
  .mc-status svg { width: 0.65rem; height: 0.65rem; flex-shrink: 0; }
  .mc-status--active    { background: rgba(16,185,129,0.12); border-color: rgba(16,185,129,0.28); color: #34D399; }
  .mc-status--inactive  { background: rgba(251,191,36,0.12); border-color: rgba(251,191,36,0.28); color: #FBBF24; }
  .mc-status--suspended { background: rgba(251,113,133,0.12); border-color: rgba(251,113,133,0.28); color: #FB7185; }
  .mc-status--terminated{ background: rgba(71,85,105,0.15); border-color: rgba(71,85,105,0.28); color: #64748B; }
  .mc-status--pending   { background: rgba(251,191,36,0.10); border-color: rgba(251,191,36,0.22); color: #FBBF24; }

  /* Parent badges */
  .mc-parent-badge {
    display: inline-flex; align-items: center; gap: 0.3rem;
    padding: 0.2rem 0.55rem; border-radius: 0.45rem;
    font-size: 0.7rem; font-weight: 600; border: 1px solid transparent;
  }
  .mc-parent-badge--reseller { background: rgba(167,139,250,0.10); border-color: rgba(167,139,250,0.25); color: var(--purple); }
  .mc-parent-badge--engine   { background: rgba(56,189,248,0.10); border-color: rgba(56,189,248,0.25); color: var(--sky); }
  .mc-parent-badge--admin    { background: rgba(255,255,255,0.04); border-color: var(--border-md); color: var(--t3); }

  /* Feature pills */
  .mc-feature {
    display: inline-block; padding: 0.15rem 0.45rem; border-radius: 0.375rem;
    font-size: 0.68rem; font-weight: 600; margin-right: 0.25rem;
    background: rgba(34,211,238,0.08); border: 1px solid rgba(34,211,238,0.18); color: var(--cyan);
  }

  /* Fee / settlement text */
  .mc-text-sm { font-size: 0.8rem; color: var(--t2); }

  /* Action button */
  .mc-action-btn {
    width: 1.75rem; height: 1.75rem; border-radius: 0.45rem;
    border: 1px solid rgba(34,211,238,0.18); background: rgba(34,211,238,0.07);
    display: flex; align-items: center; justify-content: center; cursor: pointer;
    transition: background 0.18s, border-color 0.18s, box-shadow 0.18s;
  }
  .mc-action-btn svg { width: 0.85rem; height: 0.85rem; color: var(--cyan); }
  .mc-action-btn:hover {
    background: rgba(34,211,238,0.14); border-color: rgba(34,211,238,0.38);
    box-shadow: 0 0 12px rgba(34,211,238,0.18);
  }

  /* Dropdown - scoped inside mc-root */
  .mc-dropdown-content {
    background: #0A1628 !important;
    border: 1px solid rgba(255,255,255,0.09) !important;
    border-radius: 0.75rem !important;
    box-shadow: 0 12px 40px rgba(0,0,0,0.7), 0 0 0 1px rgba(34,211,238,0.06) !important;
    padding: 0.3rem !important;
    min-width: 158px !important;
  }
  .mc-dropdown-item {
    display: flex !important; align-items: center !important; gap: 0.5rem !important;
    padding: 0.48rem 0.75rem !important; font-size: 0.8rem !important; font-weight: 500 !important;
    color: #93B4D4 !important; border-radius: 0.5rem !important;
    cursor: pointer !important; font-family: 'Outfit', system-ui, sans-serif !important;
    transition: background 0.15s, color 0.15s !important; outline: none !important;
    text-decoration: none !important;
  }
  .mc-dropdown-item svg {
    width: 0.8rem !important; height: 0.8rem !important; flex-shrink: 0 !important;
    color: #4D6E90 !important;
  }
  .mc-dropdown-item:hover svg, .mc-dropdown-item:focus svg { color: #22D3EE !important; }
  .mc-dropdown-item:hover, .mc-dropdown-item:focus {
    background: rgba(34,211,238,0.10) !important; color: #22D3EE !important;
  }
  .mc-dropdown-item--danger { color: #93B4D4 !important; }
  .mc-dropdown-item--danger:hover, .mc-dropdown-item--danger:focus {
    background: rgba(251,113,133,0.10) !important; color: #FB7185 !important;
  }
  .mc-dropdown-item--danger:hover svg, .mc-dropdown-item--danger:focus svg { color: #FB7185 !important; }
  .mc-dropdown-sep { height: 1px !important; background: rgba(255,255,255,0.06) !important; margin: 0.25rem 0 !important; }

  /* Global override for Radix portal-rendered dropdown (outside mc-root) */
  [data-radix-popper-content-wrapper] .mc-dropdown-content {
    background: #0A1628 !important;
    border: 1px solid rgba(255,255,255,0.09) !important;
    border-radius: 0.75rem !important;
    box-shadow: 0 12px 40px rgba(0,0,0,0.7) !important;
  }
  [data-radix-popper-content-wrapper] .mc-dropdown-item {
    color: #93B4D4 !important; background: transparent !important;
    font-family: 'Outfit', system-ui, sans-serif !important;
  }
  [data-radix-popper-content-wrapper] .mc-dropdown-item[data-highlighted],
  [data-radix-popper-content-wrapper] .mc-dropdown-item:hover {
    background: rgba(34,211,238,0.10) !important; color: #22D3EE !important;
  }
  [data-radix-popper-content-wrapper] .mc-dropdown-item--danger[data-highlighted],
  [data-radix-popper-content-wrapper] .mc-dropdown-item--danger:hover {
    background: rgba(251,113,133,0.10) !important; color: #FB7185 !important;
  }

  /* Loading state */
  .mc-loading {
    padding: 3rem 1rem; display: flex; flex-direction: column; align-items: center; gap: 0.75rem;
    color: var(--t3);
  }
  .mc-loading svg { color: var(--cyan); width: 2rem; height: 2rem; }

  .mc-empty {
    padding: 3rem 1rem; text-align: center; color: var(--t3); font-size: 0.875rem;
  }

  /* Scroll */
  .mc-table-scroll { overflow-x: auto; }
  .mc-table-scroll::-webkit-scrollbar { height: 5px; }
  .mc-table-scroll::-webkit-scrollbar-track { background: transparent; }
  .mc-table-scroll::-webkit-scrollbar-thumb { background: rgba(34,211,238,0.18); border-radius: 99px; }
  .mc-table-scroll::-webkit-scrollbar-thumb:hover { background: rgba(34,211,238,0.35); }

  /* Alert dialog - hardcoded values since it renders in portal */
  .mc-alert-content {
    background: #0E1C35 !important; border: 1px solid rgba(255,255,255,0.09) !important;
    border-radius: 1rem !important; color: #EFF6FF !important;
  }
  .mc-alert-title { color: #EFF6FF !important; font-weight: 700 !important; }
  .mc-alert-desc  { color: #93B4D4 !important; }
  .mc-alert-cancel {
    background: #122040 !important; border: 1px solid rgba(255,255,255,0.09) !important;
    color: #93B4D4 !important; border-radius: 0.625rem !important;
    font-family: 'Outfit', system-ui, sans-serif !important; cursor: pointer;
    transition: border-color 0.2s, color 0.2s !important;
  }
  .mc-alert-cancel:hover { border-color: rgba(34,211,238,0.22) !important; color: #EFF6FF !important; }
  .mc-alert-confirm {
    background: rgba(251,113,133,0.12) !important; border: 1px solid rgba(251,113,133,0.28) !important;
    color: #FB7185 !important; border-radius: 0.625rem !important;
    font-family: 'Outfit', system-ui, sans-serif !important; font-weight: 700 !important; cursor: pointer;
    transition: background 0.2s, border-color 0.2s, box-shadow 0.2s !important;
  }
  .mc-alert-confirm:hover {
    background: rgba(251,113,133,0.20) !important; border-color: rgba(251,113,133,0.50) !important;
    box-shadow: 0 0 14px rgba(251,113,133,0.18) !important;
  }
`;

/* ── Status config ── */
const getStatusBadge = (status) => {
  const s = (status || "").toUpperCase();
  if (s === "ACTIVE") return { cls: "mc-status--active", icon: CheckCircle2, label: "Active" };
  if (s === "INACTIVE") return { cls: "mc-status--inactive", icon: Clock, label: "Inactive" };
  if (s === "SUSPENDED") return { cls: "mc-status--suspended", icon: AlertTriangle, label: "Suspended" };
  if (s === "TERMINATED") return { cls: "mc-status--terminated", icon: XCircle, label: "Terminated" };
  return { cls: "mc-status--pending", icon: Clock, label: "Pending" };
};

export default function Merchants() {
  const [search, setSearch] = useState("");
  const [formOpen, setFormOpen] = useState(false);
  const [selectedMerchant, setSelectedMerchant] = useState(null);
  const [deleteDialog, setDeleteDialog] = useState(null);
  const [apiKeysDialog, setApiKeysDialog] = useState(null);
  const [apiError, setApiError] = useState(null);
  const { t } = useLanguage();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const urlParams = new URLSearchParams(window.location.search);
  const editId = urlParams.get("edit");

  const { data: merchants = [], isLoading, error } = useQuery({
    queryKey: ["merchants"],
    queryFn: () => adminService.getAllMerchants(),
    enabled: auth.isAuthenticated(),
    retry: 2,
    refetchOnMount: true,
    staleTime: 0,
  });

  React.useEffect(() => {
    if (editId && merchants.length > 0) {
      const merchantToEdit = merchants.find(m => m.id === editId);
      if (merchantToEdit && !formOpen) {
        setSelectedMerchant(merchantToEdit);
        setFormOpen(true);
      }
    }
  }, [editId, merchants]);


  const { data: engines = [] } = useQuery({
    queryKey: ["engines"],
    queryFn: () => adminService.getAllEngines(),
  });

  const { data: resellers = [] } = useQuery({
    queryKey: ["resellers"],
    queryFn: () => adminService.getAllResellers(),
  });

  const createMutation = useMutation({
    mutationFn: (data) => adminService.createMerchant(data),
    onSuccess: () => { queryClient.invalidateQueries(["merchants"]); setFormOpen(false); setApiError(null); },
    onError: (error) => setApiError(error?.response?.data?.message || error?.message || "Failed to create merchant"),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => adminService.updateMerchant(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries(["merchants"]);
      setFormOpen(false); setSelectedMerchant(null); setApiError(null);
    },
    onError: (error) => setApiError(error?.response?.data?.message || error?.message || "Failed to update merchant"),
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => adminService.deleteMerchant(id),
    onSuccess: () => {
      queryClient.invalidateQueries(["merchants"]);
      setDeleteDialog(null);
      toast({
        title: "Success",
        description: t("merchantDeleted") || "Merchant deleted successfully",
      });
    },
    onError: (error) => {
      setDeleteDialog(null);
      toast({
        title: "Error",
        description: error?.response?.data?.message || error?.message || "Failed to delete merchant",
        variant: "destructive",
      });
    }
  });

  const filteredMerchants = merchants.filter((m) =>
    m.name?.toLowerCase().includes(search.toLowerCase()) ||
    m.code?.toLowerCase().includes(search.toLowerCase())
  );

  const handleSave = async (data) => {
    setApiError(null);
    try {
      if (selectedMerchant) {
        await updateMutation.mutateAsync({ id: selectedMerchant.id, data });
      } else {
        await createMutation.mutateAsync(data);
      }
    } catch (err) {
      // handeled in onError
    }
  };

  const handleEdit = (merchant) => { setSelectedMerchant(merchant); setFormOpen(true); setApiError(null); };

  const handleToggleStatus = async (merchant) => {
    const newStatus = merchant.status?.toUpperCase() === "ACTIVE" ? "SUSPENDED" : "ACTIVE";
    await updateMutation.mutateAsync({ id: merchant.id, data: { name: merchant.name, status: newStatus } });
  };

  return (
    <>
      <style>{MC_CSS}</style>
      <div className="max-w-[1440px] mx-auto md:p-8 flex flex-col gap-6">

        {/* ── Header ── */}
        <div className="flex flex-col justify-between gap-4 sm:flex-row">
          <div className="mc-title-row">
            <div className="mc-icon-badge"><Building2 /></div>
            <div>
              <h1 className="mc-title">{t("merchants")}</h1>
              <p className="mc-subtitle">
                {isLoading
                  ? <span style={{ display: "flex", alignItems: "center", gap: "0.5rem", color: "var(--t3)" }}>
                    <Loader2 style={{ width: "0.875rem", height: "0.875rem", animation: "spin 1s linear infinite" }} />
                    {t("loading")}
                  </span>
                  : `${merchants.length} ${t("merchantsRegistered")}`}
              </p>
            </div>
          </div>
          <button className="mc-add-btn" onClick={() => setFormOpen(true)}>
            <Plus />
            <span className="mc-hide-xs">{t("addMerchant")}</span>
            <span className="mc-show-xs" style={{ display: "none" }}>{t("add")}</span>
          </button>
        </div>

        <hr className="mc-divider" />

        {/* ── Error ── */}
        {error && (
          <div className="mc-error">
            <strong>Error loading merchants</strong>
            <p>{error.message || "Failed to fetch merchants"}</p>
          </div>
        )}

        {/* ── Search ── */}
        <div className="mc-search-wrap">
          <Search className="mc-search-icon" />
          <input
            className="mc-search-input"
            placeholder={t("searchMerchants")}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        {/* ── Table ── */}
        <div className="mc-table-card">
          <div className="mc-table-scroll">
            <table className="mc-table">
              <thead>
                <tr className="mc-thead-row">
                  <th className="mc-th">{t("merchant")}</th>
                  <th className="mc-th">{t("code")}</th>
                  <th className="mc-th">{t("parent")}</th>
                  <th className="mc-th">{t("status")}</th>
                  <th className="mc-th">{t("features")}</th>
                  <th className="mc-th">{t("fee")}</th>
                  <th className="mc-th">{t("settlement")}</th>
                  <th className="mc-th" style={{ width: "3rem" }} />
                </tr>
              </thead>
              <tbody>
                {filteredMerchants.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="mc-empty">
                      {merchants.length === 0 ? t("noMerchantsFound") : t("noMerchantsMatchSearch")}
                    </td>
                  </tr>
                ) : (
                  filteredMerchants.map((merchant, index) => {
                    const statusBadge = getStatusBadge(merchant.status);
                    const StatusIcon = statusBadge.icon;

                    return (
                      <motion.tr
                        key={merchant.id}
                        className="mc-tbody-row"
                        initial={{ opacity: 0, y: 6 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: Math.min(index * 0.025, 0.4), duration: 0.25 }}
                      >
                        {/* Merchant name */}
                        <td className="mc-td mc-first-td">
                          <div className="mc-name-cell">
                            <div className="mc-merchant-icon"><Building2 /></div>
                            <div style={{ minWidth: 0 }}>
                              <p className="mc-merchant-name">{merchant.name}</p>
                              <p className="mc-merchant-email">{merchant.contactEmail || t("noEmail")}</p>
                            </div>
                          </div>
                        </td>

                        {/* Code */}
                        <td className="mc-td">
                          <span className="mc-code-badge">{merchant.code}</span>
                        </td>

                        {/* Parent */}
                        <td className="mc-td">
                          {(() => {
                            if (merchant.createdByResellerId) {
                              const reseller = resellers.find((r) => r.id === merchant.createdByResellerId);
                              return (
                                <span className="mc-parent-badge mc-parent-badge--reseller">
                                  Reseller: {reseller?.name || merchant.createdByResellerId.substring(0, 8)}
                                </span>
                              );
                            }
                            if (merchant.createdByEngineId) {
                              const engine = engines.find((e) => e.id === merchant.createdByEngineId);
                              return (
                                <span className="mc-parent-badge mc-parent-badge--engine">
                                  Engine: {engine?.name || merchant.createdByEngineId.substring(0, 8)}
                                </span>
                              );
                            }
                            if (merchant.createdByAdminId) {
                              return <span className="mc-parent-badge mc-parent-badge--admin">{t("directAdmin")}</span>;
                            }
                            return <span style={{ fontSize: "0.75rem", color: "var(--t4)" }}>—</span>;
                          })()}
                        </td>

                        {/* Status */}
                        <td className="mc-td">
                          <span className={`mc-status ${statusBadge.cls}`}>
                            <StatusIcon />
                            {statusBadge.label}
                          </span>
                        </td>

                        {/* Features */}
                        <td className="mc-td">
                          <div style={{ display: "flex", gap: "0.25rem", flexWrap: "wrap" }}>
                            {merchant.depositEnabled && <span className="mc-feature">{t("deposit")}</span>}
                            {merchant.withdrawalEnabled && <span className="mc-feature">{t("withdrawal")}</span>}
                            {!merchant.depositEnabled && !merchant.withdrawalEnabled &&
                              <span style={{ fontSize: "0.75rem", color: "var(--t4)" }}>—</span>}
                          </div>
                        </td>

                        {/* Fee */}
                        <td className="mc-td">
                          <span className="mc-text-sm">
                            {merchant.fixedFee || merchant.percentageFee} {merchant?.feeType === "percentage" ? "%" : " THB"}
                          </span>
                        </td>

                        {/* Settlement */}
                        <td className="mc-td">
                          <span className="mc-text-sm">{merchant.settlementPeriod || "—"}</span>
                        </td>

                        {/* Actions */}
                        <td className="mc-td">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <button className="mc-action-btn">
                                <MoreHorizontal />
                              </button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="mc-dropdown-content">
                              <DropdownMenuItem asChild className="mc-dropdown-item">
                                <Link to={createPageUrl("AdminMerchantDetail") + `?id=${merchant.id}`}
                                  style={{ display: "flex", alignItems: "center", gap: "0.5rem", textDecoration: "none", color: "inherit" }}>
                                  <Eye />
                                  {t("viewDetails")}
                                </Link>
                              </DropdownMenuItem>
                              <DropdownMenuItem className="mc-dropdown-item" onClick={() => handleEdit(merchant)}>
                                <Pencil />
                                {t("edit")}
                              </DropdownMenuItem>
                              <DropdownMenuItem className="mc-dropdown-item" onClick={() => setApiKeysDialog(merchant)}>
                                <Key />
                                {t("viewApiKeys")}
                              </DropdownMenuItem>
                              <div className="mc-dropdown-sep" />
                              <DropdownMenuItem className="mc-dropdown-item" onClick={() => handleToggleStatus(merchant)}>
                                {merchant.status?.toUpperCase() === "ACTIVE" ? (
                                  <><PowerOff />{t("suspend")}</>
                                ) : (
                                  <><Power />{t("activate")}</>
                                )}
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </td>
                      </motion.tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* ── Merchant Form ── */}
        <MerchantForm
          merchant={selectedMerchant}
          open={formOpen}
          onClose={() => { setFormOpen(false); setSelectedMerchant(null); setApiError(null); }}
          onSave={handleSave}
          engines={engines}
          resellers={resellers}
          apiError={apiError}
          onClearError={() => setApiError(null)}
        />

        {/* ── Delete Confirmation ── */}
        <AlertDialog open={!!deleteDialog} onOpenChange={() => setDeleteDialog(null)}>
          <AlertDialogContent className="mc-alert-content">
            <AlertDialogHeader>
              <AlertDialogTitle className="mc-alert-title">{t("deleteMerchant")}</AlertDialogTitle>
              <AlertDialogDescription className="mc-alert-desc">
                {t("deleteConfirmation")} "{deleteDialog?.name}"? {t("cannotBeUndone")}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel className="mc-alert-cancel">{t("cancel")}</AlertDialogCancel>
              <AlertDialogAction
                className="mc-alert-confirm"
                onClick={() => deleteMutation.mutate(deleteDialog.id)}
              >
                {t("delete")}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* ── API Keys Dialog ── */}
        <Dialog open={!!apiKeysDialog} onOpenChange={(open) => !open && setApiKeysDialog(null)}>
          <DialogContent className="mc-alert-content bg-[#0E1C35] border-white/10 text-white max-w-md">
            <DialogHeader>
              <DialogTitle className="mc-alert-title text-xl font-bold flex items-center gap-2">
                <Key className="w-5 h-5 text-cyan-400" />
                API Keys
              </DialogTitle>
              <DialogDescription className="text-gray-400 mt-2">
                These are the production keys for {apiKeysDialog?.name}. Keep them secure.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 mt-4">
              <div>
                <label className="text-sm font-medium text-gray-400 mb-1 block">Public Key</label>
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    readOnly
                    value={apiKeysDialog?.apiKey || "Not set"}
                    className="w-full bg-[#122040] border border-white/10 rounded-md px-3 py-2 text-sm text-gray-200 focus:outline-none"
                  />
                  <button
                    onClick={() => {
                      navigator.clipboard.writeText(apiKeysDialog?.apiKey || "");
                      toast({ title: "Copied", description: "Public key copied to clipboard" });
                    }}
                    title="Copy Public Key"
                    className="p-2 border border-white/10 rounded-md bg-[#122040] hover:bg-white/5 text-gray-300 transition-colors"
                  >
                    <Copy className="w-4 h-4" />
                  </button>
                </div>
              </div>

              <div>
                <label className="text-sm font-medium text-gray-400 mb-1 block">Secret Key</label>
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    readOnly
                    value={apiKeysDialog?.secretKey || "Not set"}
                    className="w-full bg-[#122040] border border-white/10 rounded-md px-3 py-2 text-sm text-gray-200 focus:outline-none"
                  />
                  <button
                    onClick={() => {
                      navigator.clipboard.writeText(apiKeysDialog?.secretKey || "");
                      toast({ title: "Copied", description: "Secret key copied to clipboard" });
                    }}
                    title="Copy Secret Key"
                    className="p-2 border border-white/10 rounded-md bg-[#122040] hover:bg-white/5 text-gray-300 transition-colors"
                  >
                    <Copy className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        <Toaster />
      </div>
    </>
  );
}